import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import api from "../api/api";

const SettingsContext = createContext();
export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  /* =====================================================================================
      VERIFICAR VISIBILIDADE DO BOTÃO FLUTUANTE
  ===================================================================================== */
  const checkContactButtonVisibility = useMemo(() => {
    return (currentPath) => {
      const path =
        (currentPath || "/")
          .split("?")[0]
          .toLowerCase()
          .replace(/\/$/, "") || "/";

      const rules = (settings.CONTACT_BUTTON_PAGES || "*")
        .toLowerCase()
        .split("\n")
        .map((r) => r.trim())
        .filter(Boolean);

      if (!settings.CONTACT_BUTTON_ENABLED) return false;

      let isIncluded = false;
      let isExcluded = false;

      for (const rule of rules) {
        let rulePath = rule;
        const isExclusion = rule.startsWith("!");

        if (isExclusion) rulePath = rule.substring(1).trim();

        rulePath = rulePath.replace(/\/$/, "") || "/";

        const isWildcard = rulePath.endsWith("/*");
        const ruleBase = isWildcard ? rulePath.slice(0, -2) : rulePath;

        let match = false;

        if (rule === "*") match = true;
        else if (isWildcard)
          match = path === ruleBase || path.startsWith(ruleBase + "/");
        else match = path === ruleBase;

        if (match) {
          if (isExclusion) isExcluded = true;
          else isIncluded = true;
        }
      }

      if (isExcluded) return false;
      if (isIncluded) return true;

      return rules.includes("*");
    };
  }, [settings.CONTACT_BUTTON_PAGES, settings.CONTACT_BUTTON_ENABLED]);

  /* =====================================================================================
      NORMALIZE SETTINGS
  ===================================================================================== */
  const normalize = (raw = {}) => {
    let parsedWidgets = [];
    try {
      if (raw.FOOTER_WIDGETS) {
        parsedWidgets = Array.isArray(raw.FOOTER_WIDGETS)
          ? raw.FOOTER_WIDGETS
          : JSON.parse(raw.FOOTER_WIDGETS);
      }
    } catch {
      parsedWidgets = [];
    }

    let showType = raw.SHOW_LOGO_TYPE || raw.SHOW_LOGO || null;

    if (!showType) {
      if (raw.SHOW_LOGO_AND_NAME === true || raw.SHOW_LOGO_AND_NAME === "true")
        showType = "logo_and_name";
      else if (raw.SHOW_LOGO_ONLY === true || raw.SHOW_LOGO_ONLY === "true")
        showType = "logo_only";
      else showType = raw.SITE_LOGO ? "logo_and_name" : "name_only";
    }

    return {
      SITE_NAME: raw.SITE_NAME || "",
      SITE_LOGO: raw.SITE_LOGO || "",
      SITE_LOGO_SIZE: Number(raw.SITE_LOGO_SIZE || 80),
      SHOW_LOGO_TYPE: showType,

      HEADER_THEME: raw.HEADER_THEME || raw.DEFAULT_THEME || "indigo",
      HEADER_BG_COLOR: raw.HEADER_BG_COLOR || "",
      HEADER_BG_GRADIENT: raw.HEADER_BG_GRADIENT || "",
      HEADER_EFFECT: raw.HEADER_EFFECT || "none",
      HEADER_TEXT_COLOR: raw.HEADER_TEXT_COLOR || "#ffffff",

      HOME_SLIDER_ENABLED: !!raw.HOME_SLIDER_ENABLED,
      MAINTENANCE_MODE: !!raw.MAINTENANCE_MODE,

      FOOTER_ENABLED:
        raw.FOOTER_ENABLED === undefined ? true : !!raw.FOOTER_ENABLED,
      FOOTER_MODE: raw.FOOTER_MODE || "simple",
      FOOTER_TEXT: raw.FOOTER_TEXT || "",
      FOOTER_LINKS: raw.FOOTER_LINKS || "",
      FOOTER_WIDGETS: parsedWidgets,
      FOOTER_BG_COLOR: raw.FOOTER_BG_COLOR || "",
      FOOTER_BG_GRADIENT: raw.FOOTER_BG_GRADIENT || "",
      FOOTER_TEXT_COLOR: raw.FOOTER_TEXT_COLOR || "#ffffff",
      FOOTER_EFFECT: raw.FOOTER_EFFECT || "none",

      MENU_ITEMS: raw.MENU_ITEMS || "",

      CONTACT_BUTTON_ENABLED: !!raw.CONTACT_BUTTON_ENABLED,
      CONTACT_BUTTON_TEXT: raw.CONTACT_BUTTON_TEXT || "Fale Conosco",
      CONTACT_BUTTON_COLOR: raw.CONTACT_BUTTON_COLOR || "#4f46e5",
      CONTACT_BUTTON_ICON: raw.CONTACT_BUTTON_ICON || "",
      CONTACT_BUTTON_LAYOUT: raw.CONTACT_BUTTON_LAYOUT || "pill",
      CONTACT_BUTTON_POSITION: raw.CONTACT_BUTTON_POSITION || "bottom-right",
      CONTACT_BUTTON_SIZE: raw.CONTACT_BUTTON_SIZE || "medium",
      CONTACT_BUTTON_PAGES: raw.CONTACT_BUTTON_PAGES || "*",
      CONTACT_MODAL_CONTENT: raw.CONTACT_MODAL_CONTENT || "[form id=1]",

      ...Object.fromEntries(
        Object.entries(raw).filter(([key]) => key !== "FOOTER_WIDGETS")
      ),
    };
  };

  /* =====================================================================================
      CARREGAMENTO INICIAL DE SETTINGS (ROTAS PÚBLICAS)
  ===================================================================================== */
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      let res = null;

      try {
        // 1) Tenta rota pública principal
        res = await api.get("/global/settings");
      } catch {
        try {
          // 2) fallback
          res = await api.get("/settings/public");
        } catch {
          // 3) fallback final
          res = await api.get("/api/v1/settings/public");
        }
      }

      try {
        const raw =
          (res && res.data && res.data.settings) ||
          (res && res.data) ||
          {};

        if (mounted) setSettings(normalize(raw));
      } catch (err) {
        console.error("Erro ao processar settings:", err);
        if (mounted) setSettings(normalize({}));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  /* =====================================================================================
      EXPORTAÇÃO DO CONTEXTO
  ===================================================================================== */
  const contextValue = useMemo(
    () => ({
      settings,
      loading,
      setSettings,
      checkContactButtonVisibility,
    }),
    [settings, loading, checkContactButtonVisibility]
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};
